import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, useToast } from '@caruso/ui';
import {
  positionsService,
  CreatePositionDto,
  UpdatePositionDto,
  PositionProcess,
} from '@/services/positions.service';
import { processesService, Process } from '@/services/processes.service';
import {
  ArrowLeft,
  Save,
  Briefcase,
  Building2,
  TrendingUp,
  DollarSign,
  Gift,
  CheckSquare,
  Workflow,
  Plus,
  Trash2,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@caruso/ui';

const positionSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  description: z.string().optional(),
  department: z.string().optional(),
  level: z.string().optional(),
  baseSalary: z.string().optional(),
  benefits: z.string().optional(),
  requirements: z.string().optional(),
  active: z.boolean(),
});

type PositionFormData = z.infer<typeof positionSchema>;

export function PositionFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = !!id;

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<'informacoes' | 'processos'>('informacoes');

  // Process association state
  const [isAddingProcess, setIsAddingProcess] = useState(false);
  const [searchProcess, setSearchProcess] = useState('');
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);
  const [filteredProcesses, setFilteredProcesses] = useState<Process[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<Process[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm<PositionFormData>({
    resolver: zodResolver(positionSchema),
    defaultValues: {
      active: true,
    }
  });

  // Load position data if editing
  const { data: position, isLoading: isLoadingPosition } = useQuery({
    queryKey: ['position', id],
    queryFn: () => positionsService.getById(id!),
    enabled: isEdit,
  });

  // Load associated processes
  const {
    data: associatedProcesses = [],
    isLoading: isLoadingProcesses,
    refetch: refetchProcesses
  } = useQuery({
    queryKey: ['position-processes', id],
    queryFn: () => positionsService.getProcesses(id!),
    enabled: isEdit && activeSubTab === 'processos',
  });

  // Load all processes for dropdown
  const { data: allProcesses } = useQuery({
    queryKey: ['processes-all'],
    queryFn: () => processesService.getAll({ active: true }),
  });

  // Set form values when editing
  useEffect(() => {
    if (position) {
      reset({
        name: position.name,
        description: position.description || '',
        department: position.department || '',
        level: position.level || '',
        baseSalary: position.baseSalary?.toString() || '',
        benefits: position.benefits || '',
        requirements: position.requirements || '',
        active: position.active,
      });
    }
  }, [position, reset]);

  // Search processes effect
  useEffect(() => {
    if (searchProcess && allProcesses) {
      const filtered = allProcesses.filter(process =>
        process.name.toLowerCase().includes(searchProcess.toLowerCase()) ||
        process.description?.toLowerCase().includes(searchProcess.toLowerCase())
      );
      setFilteredProcesses(filtered);
      setShowProcessDropdown(filtered.length > 0);
    } else {
      setFilteredProcesses([]);
      setShowProcessDropdown(false);
    }
  }, [searchProcess, allProcesses]);

  const createMutation = useMutation({
    mutationFn: (data: CreatePositionDto) => positionsService.create(data),
    onSuccess: () => {
      toast({
        title: 'Cargo criado com sucesso',
        variant: 'success',
      });
      navigate('/app/cargos');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar cargo',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePositionDto) => positionsService.update(id!, data),
    onSuccess: () => {
      toast({
        title: 'Cargo atualizado com sucesso',
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar cargo',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: PositionFormData) => {
    const payload = {
      ...data,
      baseSalary: data.baseSalary ? parseFloat(data.baseSalary) : undefined,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleToggleProcessSelection = (process: Process) => {
    setSelectedProcesses((prev) => {
      const exists = prev.find((p) => p.id === process.id);
      if (exists) {
        return prev.filter((p) => p.id !== process.id);
      } else {
        return [...prev, process];
      }
    });
  };

  const handleAddProcesses = async () => {
    if (selectedProcesses.length === 0) {
      toast({
        title: 'Selecione pelo menos um processo',
        variant: 'destructive',
      });
      return;
    }

    if (!id) return;

    try {
      const processIds = selectedProcesses.map(p => p.id);
      await positionsService.associateMultipleProcesses(id, processIds);
      toast({
        title: 'Processos associados com sucesso',
        variant: 'success',
      });
      refetchProcesses();
      setSelectedProcesses([]);
      setSearchProcess('');
      setIsAddingProcess(false);
    } catch (error: any) {
      toast({
        title: 'Erro ao associar processos',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveProcess = async (processId: string) => {
    if (!id) return;

    if (!confirm('Tem certeza que deseja remover este processo?')) {
      return;
    }

    try {
      await positionsService.removeProcessAssociation(id, processId);
      toast({
        title: 'Processo removido com sucesso',
        variant: 'success',
      });
      refetchProcesses();
    } catch (error) {
      toast({
        title: 'Erro ao remover processo',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingPosition) {
    return <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/app/cargos')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-serif text-brand-gold">
          {isEdit ? 'Editar Cargo' : 'Novo Cargo'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Position Information */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Informações do Cargo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="name" className="text-brand-ivory">
                  Nome *
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Ex: Gerente de Vendas"
                />
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description" className="text-brand-ivory">
                  Descrição
                </Label>
                <textarea
                  id="description"
                  {...register('description')}
                  className="w-full bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory min-h-[100px] rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                  placeholder="Descrição detalhada do cargo..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department" className="text-brand-ivory flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  Departamento
                </Label>
                <Input
                  id="department"
                  {...register('department')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Ex: Comercial"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="level" className="text-brand-ivory flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  Nível
                </Label>
                <Input
                  id="level"
                  {...register('level')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Ex: Pleno, Sênior"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="baseSalary" className="text-brand-ivory flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Salário Base
                </Label>
                <Input
                  id="baseSalary"
                  {...register('baseSalary')}
                  type="number"
                  step="0.01"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="benefits" className="text-brand-ivory flex items-center gap-1">
                  <Gift className="w-4 h-4" />
                  Benefícios
                </Label>
                <Input
                  id="benefits"
                  {...register('benefits')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                  placeholder="Ex: VT, VR, Plano de Saúde"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="requirements" className="text-brand-ivory flex items-center gap-1">
                  <CheckSquare className="w-4 h-4" />
                  Requisitos
                </Label>
                <textarea
                  id="requirements"
                  {...register('requirements')}
                  className="w-full bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory min-h-[100px] rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
                  placeholder="Requisitos necessários para o cargo..."
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    {...register('active')}
                    className="w-4 h-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                  />
                  <Label htmlFor="active" className="text-brand-ivory cursor-pointer">
                    Cargo ativo
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sub-tabs for Information and Processes */}
        {isEdit && (
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <div className="flex gap-4 border-b border-brand-gold/20">
                <button
                  type="button"
                  onClick={() => setActiveSubTab('informacoes')}
                  className={cn(
                    'pb-2 px-1 text-sm font-medium transition-colors relative',
                    activeSubTab === 'informacoes'
                      ? 'text-brand-gold'
                      : 'text-brand-ivory/50 hover:text-brand-ivory'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Informações
                  </span>
                  {activeSubTab === 'informacoes' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSubTab('processos')}
                  className={cn(
                    'pb-2 px-1 text-sm font-medium transition-colors relative',
                    activeSubTab === 'processos'
                      ? 'text-brand-gold'
                      : 'text-brand-ivory/50 hover:text-brand-ivory'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Workflow className="w-4 h-4" />
                    Processos Associados
                  </span>
                  {activeSubTab === 'processos' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold" />
                  )}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Information Sub-tab Content */}
              {activeSubTab === 'informacoes' && (
                <div className="text-center py-8 text-brand-ivory/50">
                  As informações do cargo são exibidas no formulário acima.
                </div>
              )}

              {/* Processes Sub-tab Content */}
              {activeSubTab === 'processos' && (
                <div className="space-y-4">
                  {/* Add Process Button */}
                  {!isAddingProcess && (
                    <Button
                      type="button"
                      onClick={() => setIsAddingProcess(true)}
                      className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Processos
                    </Button>
                  )}

                  {/* Add Process Form */}
                  {isAddingProcess && (
                    <div className="border border-brand-gold/20 rounded-lg p-4 space-y-4 bg-brand-smoke/30">
                      <div className="flex items-center justify-between">
                        <h4 className="text-brand-gold font-medium">Adicionar Processos</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setIsAddingProcess(false);
                            setSelectedProcesses([]);
                            setSearchProcess('');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Process Search */}
                      <div className="relative">
                        <Label className="text-brand-ivory">Buscar Processos</Label>
                        <div className="relative mt-2">
                          <Input
                            value={searchProcess}
                            onChange={(e) => setSearchProcess(e.target.value)}
                            placeholder="Buscar processos..."
                            className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                          />
                          <Search className="w-4 h-4 text-brand-gold/50 absolute left-3 top-1/2 -translate-y-1/2" />

                          {showProcessDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-md shadow-lg max-h-60 overflow-auto">
                              {filteredProcesses.map(process => {
                                const isSelected = selectedProcesses.find(p => p.id === process.id);
                                const isAlreadyAssociated = associatedProcesses.find(
                                  ap => ap.process.id === process.id
                                );

                                return (
                                  <button
                                    key={process.id}
                                    type="button"
                                    onClick={() => {
                                      if (!isAlreadyAssociated) {
                                        handleToggleProcessSelection(process);
                                      }
                                    }}
                                    disabled={!!isAlreadyAssociated}
                                    className={cn(
                                      'w-full text-left px-4 py-2 text-brand-ivory',
                                      isAlreadyAssociated
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-brand-gold/10',
                                      isSelected && 'bg-brand-gold/20'
                                    )}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium">{process.name}</div>
                                        {process.description && (
                                          <div className="text-xs text-brand-ivory/50">
                                            {process.description}
                                          </div>
                                        )}
                                      </div>
                                      {isAlreadyAssociated && (
                                        <span className="text-xs text-brand-ivory/50">
                                          Já associado
                                        </span>
                                      )}
                                      {isSelected && !isAlreadyAssociated && (
                                        <span className="text-xs text-brand-gold">
                                          Selecionado
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Selected Processes */}
                      {selectedProcesses.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-brand-ivory">
                            Processos Selecionados ({selectedProcesses.length})
                          </Label>
                          <div className="space-y-2">
                            {selectedProcesses.map(process => (
                              <div
                                key={process.id}
                                className="flex items-center justify-between p-2 bg-brand-smoke/50 rounded border border-brand-gold/10"
                              >
                                <div>
                                  <div className="text-sm font-medium text-brand-ivory">
                                    {process.name}
                                  </div>
                                  {process.description && (
                                    <div className="text-xs text-brand-ivory/50">
                                      {process.description}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleProcessSelection(process)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-end gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsAddingProcess(false);
                            setSelectedProcesses([]);
                            setSearchProcess('');
                          }}
                          className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          onClick={handleAddProcesses}
                          className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                          disabled={selectedProcesses.length === 0}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Associated Processes List */}
                  <div className="border border-brand-gold/20 rounded-lg overflow-hidden">
                    {isLoadingProcesses ? (
                      <div className="p-8 text-center text-brand-ivory/50">
                        Carregando processos...
                      </div>
                    ) : associatedProcesses.length === 0 ? (
                      <div className="p-8 text-center text-brand-ivory/50">
                        <Workflow className="w-12 h-12 mx-auto mb-3 text-brand-gold/20" />
                        <p>Nenhum processo associado</p>
                        <p className="text-xs mt-1">
                          Clique em "Adicionar Processos" para associar processos a este cargo
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-brand-smoke/50 border-b border-brand-gold/20">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                                Nome
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                                Descrição
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                                Frequência
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-brand-gold uppercase tracking-wider">
                                Ações
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-brand-gold/10">
                            {associatedProcesses.map((item) => (
                              <tr key={item.associationId} className="hover:bg-brand-gold/5">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-brand-ivory">
                                    {item.process.name}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-brand-ivory/60 max-w-xs truncate">
                                    {item.process.description || '-'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-brand-ivory">
                                    {item.process.type || '-'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveProcess(item.process.id)}
                                    className="text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/cargos')}
            className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
